import * as React from 'react';
import { motion } from 'framer-motion';
import { PseudoBox } from '@chakra-ui/core';
import { Box, Absolute, BoxProps } from '../box';
import { Flex } from '../flex';
import { Text } from '../text';
import { Icon } from '../icon';
import { Keyboard } from '../keyboard';

const getVariantOptions = (variant: string): any => {
    let opts: any = {
        container: {
            bg: 'brand.green.base'
        },
        item: {
            borderTop: '1px solid rgba(0,0,0,.1)',
            color: 'text.light.tertiary',
            fontWeight: 'bold',
            px: 3,
            _hover: {
                color: 'text.light.primary',
                cursor: 'pointer'
            }
        },
        selectedItem: {}
    };

    if (variant === 'light') {
        opts = {
            ...opts,
            container: {
                ...opts.container,
                bg: 'brand.white.base'
            },
            item: {
                ...opts.item,
                color: 'text.dark.tertiary',
                _hover: {
                    color: 'text.dark.primary',
                    cursor: 'pointer'
                }
            }
        };
    } else if (variant === 'dark') {
        opts.container = { ...opts.container, bg: 'brand.dark.1100' };
    }

    opts.container.overflow = 'hidden';
    opts.item._hover.cursor = 'pointer';
    opts.selectedItem = {
        ...opts.item,
        borderTop: 0,
        color: opts.item._hover.color
    };

    return opts;
};

type MenuItem = {
    value?: any
    label?: string
    component?: any
}

const MotionBox = motion.custom(Box);

export interface ExpandingMenuProps extends BoxProps {
    variant?: 'brand' | 'light' | 'dark';
    items: MenuItem[];
    value?: string;
    itemHeight?: number | string;
    isModal?: boolean;
    expandedWidth?: number;
    selectedElement?: React.ReactElement;
    onSelect?: (value: any) => void;
}

const ExpandingMenu: React.FC<ExpandingMenuProps> = ({
    variant = 'brand',
    width: initialWidth = 'auto',
    height: initialHeight = 44,
    value: initialValue,
    items = [],
    itemHeight,
    isModal = true,
    rounded = 'full',
    expandedWidth: initialExpandedWidth,
    selectedElement,
    onSelect,
    ...rest
}: ExpandingMenuProps): React.ReactElement => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [width, setWidth] = React.useState(0);
    const [expandedWidth, setExpandedWidth] = React.useState(0);
    const [height, setHeight] = React.useState(parseInt(`${initialHeight}`, 10));
    const [value, setValue] = React.useState(initialValue);
    const elementRef = React.useRef(null);
    rest = rest || {};

    const defaultHeight = parseFloat(`${initialHeight}`);
    itemHeight = parseFloat(`${itemHeight}`);
    const styleOpts = getVariantOptions(variant);
    if (rounded) {
        styleOpts.container = {
            ...styleOpts.container,
            borderRadius: `${Math.ceil(height / 2)}px`
        };
    }

    const itemForValue = (value: any): MenuItem => {
        let result: MenuItem = {};
        items.forEach((item) => {
            if (item.value === value) {
                result = item;
            }
        });
        return result;
    };

    const handleClick = () => {
        const open = !isOpen;
        if (open) {
            setIsFocused(true);
        }
        setIsOpen(open);
    };

    const handleSelect = (item: MenuItem) => {
        setValue(item.value);
        if (onSelect) {
            onSelect(item);
        }
    };

    const handleEsc = () => {
        setIsOpen(false);
    };

    React.useLayoutEffect(() => {
        setHeight(Number(elementRef.current.offsetHeight) + 2);
    }, [elementRef]);

    React.useEffect(() => {
        const w = parseFloat(`${initialWidth}`);
        const xw = parseFloat(`${initialExpandedWidth || initialWidth}`);
        if (Number.isNaN(w) === false) {
            setWidth(w);
        }
        if (Number.isNaN(xw) === false) {
            setExpandedWidth(xw);
        }
    }, [initialWidth, initialExpandedWidth]);

    if (typeof value === 'undefined' && items.length > 0) {
        setValue(items[0].value);
    }

    return (
        <Keyboard onEsc={handleEsc}>
            <Box position="relative" zIndex={9000}>
                {(isFocused && isModal) && (
                    <motion.div
                        key="overlay"
                        style={{
                            background: 'rgba(0,0,0,0.85)',
                            bottom: 0,
                            left: 0,
                            position: 'fixed',
                            right: 0,
                            top: 0,
                            zIndex: 0
                        }}
                        variants={{
                            open: {
                                opacity: 1
                            },
                            closed: {
                                opacity: 0
                            }
                        }}
                        transition={{
                            duration: 0.3
                        }}
                        initial="closed"
                        animate={isOpen ? 'open' : 'closed'}
                        onAnimationComplete={() => {
                            if (!isOpen) {
                                setIsFocused(false);
                            }
                        }}
                        onTap={() => setIsOpen(false)}
                    />
                )}
                <MotionBox
                    position="relative"
                    cursor="pointer"
                    zIndex={1}
                    onClick={handleClick}
                    transition={{
                        duration: 0.3
                    }}
                    animate={width > 0 ? {
                        width: isOpen ? expandedWidth : width,
                        position: isOpen ? 'absolute' : 'relative'
                    } : {}}
                >
                    <Flex
                        height={`${defaultHeight}px`}
                        justify="space-between"
                        align="center"
                        {...styleOpts.selectedItem}
                    >
                        {selectedElement ? (
                            <Box m={0} height={`${defaultHeight}px`} position="relative" flexGrow={1}>
                                <MotionBox
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    lineHeight={`${defaultHeight}px`}
                                    animate={{
                                        opacity: isOpen ? 0 : 1
                                    }}
                                    transition={{
                                        duration: 0.3
                                    }}
                                >
                                    {React.cloneElement(selectedElement, { value, label: itemForValue(value).label })}
                                </MotionBox>
                                <MotionBox
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    height={`${itemHeight || defaultHeight}px`}
                                    lineHeight={`${itemHeight || defaultHeight}px`}
                                    animate={{
                                        opacity: isOpen ? 1 : 0
                                    }}
                                    transition={{
                                        duration: 0.3
                                    }}
                                >
                                    <Text m={0} fontWeight="bold" isTruncated>
                                        {itemForValue(value).label}
                                    </Text>
                                </MotionBox>
                            </Box>
                        ) : (
                            <Text m={0} fontWeight="bold" isTruncated>
                                {itemForValue(value).label}
                            </Text>
                        )}
                        <motion.div
                            variants={{
                                open: {
                                    rotate: 180
                                },
                                closed: {
                                    rotate: 0
                                }
                            }}
                            transition={{
                                duration: 0.3
                            }}
                            initial="closed"
                            animate={isOpen ? 'open' : 'closed'}
                        >
                            <Icon
                                mt="-4px"
                                name="chevron-down"
                                color={styleOpts.selectedItem.color}
                                height="24px"
                                width="24px"
                            />
                        </motion.div>
                    </Flex>
                    <Absolute width="100%" top="0" left="0" right="0" zIndex={-1}>
                        <MotionBox
                            {...styleOpts.container}
                            {...rest}
                            variants={{
                                open: {
                                    height,
                                    borderRadius: `${Math.ceil(defaultHeight / 2)}px`
                                },
                                closed: {
                                    height: defaultHeight,
                                    borderRadius: `${Math.ceil(defaultHeight / 2)}px`
                                }
                            }}
                            initial="closed"
                            animate={isOpen ? 'open' : 'closed'}
                            transition={{
                                type: 'spring',
                                damping: 50,
                                stiffness: 450
                            }}
                        >
                            <div ref={elementRef}>
                                <Box
                                    position="relative"
                                    pt={`${itemHeight || defaultHeight}px`}
                                >
                                    <Box borderTopColor="bg.800" borderTopStyle="solid" borderTopWidth="2px">
                                        {items
                                            .filter((item: MenuItem) => item.value !== value)
                                            .map((item: MenuItem, index: number) => (
                                                <PseudoBox
                                                    key={`item-${index}`}
                                                    height={`${itemHeight || defaultHeight}px`}
                                                    lineHeight={`${itemHeight || defaultHeight}px`}
                                                    onClick={() => handleSelect(item)}
                                                    {...styleOpts.item}
                                                >
                                                    {item.component || <Text m={0} fontWeight="bold">{item.label}</Text>}
                                                </PseudoBox>
                                            ))}
                                    </Box>
                                </Box>
                            </div>
                        </MotionBox>
                    </Absolute>
                </MotionBox>
            </Box>
        </Keyboard>
    );
};

export default ExpandingMenu;
